/**
 * BookmarksPage — Stay22 launch (2026-08-07).
 *
 * Lists rows from `bookmarks_v2` (server-side source of truth) for
 * the logged-in traveler. Reachable from:
 *   • NavDrawer → "Bookmarks"
 *   • Explore hero → "Saved" pill
 *   • Direct routes: /bookmarks and /explore/bookmarks
 *
 * Minimal-snapshot rule (Stay22 no-cold-storage compliance):
 *   The page renders ONLY the fields already in bookmarks_v2 — id,
 *   title, image, price, supplier — plus a Book Now button that fires
 *   the standard delayed-open click flow using the stored
 *   `deeplink_url`. We do NOT re-fetch full Stay22 property details
 *   here (that would defeat the "no cold storage" contract). A
 *   /accommodations/property/{id} live-refresh could be added later
 *   as an explicit user-triggered action.
 */
import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Bed, Heart, ExternalLink, Loader2, Trash2, ArrowLeft, Bookmark, Sparkles,
} from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import EarnPointsCta from "@/components/EarnPointsCta";

function priceLine(bm) {
  const p = bm.payload || {};
  const price = p.price;
  const currency = p.currency || "$";
  const supplier = p.supplier || bm.provider || "Stay22";
  if (typeof price === "number") {
    return `${supplier} · from ${currency} ${Math.round(price)}`;
  }
  // No stored price? Show supplier only — the Book Now button still
  // works because deeplink_url is Stay22-tagged.
  return supplier;
}

async function bookOne(bm) {
  const p = bm.payload || {};
  const deeplink = p.deeplink_url || p.booking_url || "";
  if (!deeplink) {
    toast.error("Booking link unavailable for this stay.");
    return;
  }
  // Booking Link Override (2026-08-14) — when this bookmark is an
  // override row, open the CJ URL BYTE-FOR-BYTE. Do NOT route through
  // /accommodations/click — that endpoint appends `campaign=<uuid>`
  // which mutates the URL and breaks the resolver's byte-preservation
  // contract.
  if (bm?.booking_override) {
    try { window.open(deeplink, "_blank", "noopener,noreferrer"); }
    catch { window.location.href = deeplink; }
    return;
  }
  let tab = null;
  try { tab = window.open("about:blank", "_blank"); } catch {}
  // Fire the standard tracked-click log so this shows up in the
  // affiliate report same as an in-search Book Now.
  try {
    const property_id = p.property_id || String(bm.id || "").replace(/^stay22:/, "");
    const { data } = await api.post("/accommodations/click", {
      property_id,
      property_name: bm.title,
      supplier: p.supplier || "Stay22",
      supplier_code: p.supplier_code || "",
      price: typeof p.price === "number" ? p.price : null,
      currency: p.currency || "",
      deeplink_used: deeplink,
      context: "bookmarks",
    });
    const url = data?.url || deeplink;
    if (tab && !tab.closed) { try { tab.location.href = url; } catch {} }
    else if (url) window.open(url, "_blank");
  } catch {
    if (tab && !tab.closed) { try { tab.location.href = deeplink; } catch {} }
    else window.open(deeplink, "_blank");
  }
}


function BookmarkRow({ bm, onRemove, onDream, dreaming }) {
  return (
    <div
      className="flex items-stretch gap-3 md:gap-4 rounded-2xl bg-white border-2 border-tb-navy/15 overflow-hidden shadow-sm"
      data-testid={`bookmark-row-${bm.id}`}
    >
      <div className="w-28 md:w-40 shrink-0 bg-tb-navy/5">
        {bm.image ? (
          <img
            src={bm.image} alt={bm.title || "Saved stay"} loading="lazy"
            className="w-full h-full object-cover"
          />
        ) : (
          <div
            className="w-full h-full flex items-center justify-center"
            style={{ background: "linear-gradient(135deg,#0f2740 0%,#1a3a5f 60%,rgba(230,184,74,0.35) 100%)" }}
          >
            <Bed className="w-8 h-8 text-white/70" strokeWidth={1.4} />
          </div>
        )}
      </div>

      <div className="flex-1 min-w-0 flex flex-col justify-between py-3 pr-3 md:pr-4">
        <div className="min-w-0">
          <div
            className="text-tb-navy text-base md:text-lg leading-tight line-clamp-2 font-semibold"
            style={{ fontFamily: '"Playfair Display", serif' }}
          >
            {bm.title || "Saved stay"}
          </div>
          {bm.subtitle && (
            <div className="mt-0.5 text-tb-navy/55 font-hand text-xs line-clamp-1">
              {bm.subtitle}
            </div>
          )}
          <div className="mt-1 text-tb-navy/70 font-hand text-xs" data-testid={`bookmark-row-price-${bm.id}`}>
            {priceLine(bm)}
          </div>
        </div>

        <div className="mt-2 flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={() => bookOne(bm)}
            data-testid={`bookmark-row-book-${bm.id}`}
            className="h-9 px-4 rounded-full bg-tb-gold-bright text-tb-navy font-hand text-xs font-semibold inline-flex items-center gap-1.5 hover:brightness-110 tb-tap"
          >
            Book Now <ExternalLink className="w-3 h-3" />
          </button>
          <button
            type="button"
            onClick={() => onDream(bm)}
            disabled={dreaming}
            data-testid={`bookmark-row-dream-${bm.id}`}
            className="h-9 px-4 rounded-full border-2 border-tb-navy/25 text-tb-navy font-hand text-xs font-semibold inline-flex items-center gap-1.5 hover:border-tb-navy hover:bg-tb-navy/[0.04] disabled:opacity-40 tb-tap"
          >
            {dreaming
              ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
              : <Sparkles className="w-3.5 h-3.5" />}
            Dream it
          </button>
          <button
            type="button"
            onClick={() => onRemove(bm)}
            data-testid={`bookmark-row-remove-${bm.id}`}
            aria-label="Remove"
            className="h-9 w-9 rounded-full border-2 border-tb-navy/20 text-tb-navy/70 inline-flex items-center justify-center hover:border-red-400 hover:text-red-500 tb-tap ml-auto"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
        <EarnPointsCta tone="light" multiplier={bm?.booking_override?.rewards_multiplier ?? 1} />
      </div>
    </div>
  );
}


export default function BookmarksPage() {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dreamingId, setDreamingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get("/bookmarks");
      setRows(Array.isArray(data?.bookmarks) ? data.bookmarks : []);
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't load your bookmarks.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const remove = useCallback(async (bm) => {
    // Optimistic remove.
    setRows((r) => r.filter((x) => x.id !== bm.id));
    try {
      await api.delete(`/bookmarks/${encodeURIComponent(bm.id)}`);
      toast.info("Removed from Bookmarks");
    } catch {
      toast.error("Couldn't remove — refresh and try again.");
      // Reload from source of truth to reconcile.
      load();
    }
  }, [load]);

  // Item 4 — "Dream it" pins the bookmark's snapshot onto the user's
  // dream board via POST /api/dream/pins. Bookmark is intentionally
  // NOT removed — a user can dream + keep it bookmarked.
  const dreamIt = useCallback(async (bm) => {
    setDreamingId(bm.id);
    const p = bm.payload || {};
    const priceStr = (typeof p.price === "number")
      ? `${p.supplier || "Stay"} · ${p.currency || "$"} ${Math.round(p.price)}`
      : (p.supplier || bm.provider || "Saved stay");
    try {
      await api.post("/dream/pins", {
        image:    bm.image || "",
        note:     priceStr,
        category: "place",
        name:     bm.title || "Saved stay",
        location: bm.subtitle || "",
      });
      toast.success("Added to your dreams", {
        action: {
          label: "View",
          onClick: () => nav("/dream"),
        },
      });
    } catch {
      toast.error("Couldn't add to dreams — try again.");
    } finally {
      setDreamingId(null);
    }
  }, [nav]);

  return (
    <div className="max-w-3xl mx-auto px-4 md:px-6 py-8" data-testid="bookmarks-page">
      <div className="flex items-center gap-3 mb-5">
        <button
          type="button"
          onClick={() => nav("/explore")}
          data-testid="bookmarks-back"
          className="h-9 w-9 rounded-full border-2 border-tb-navy/20 text-tb-navy inline-flex items-center justify-center hover:border-tb-navy tb-tap"
          aria-label="Back"
        >
          <ArrowLeft className="w-4 h-4" />
        </button>
        <h1
          className="text-3xl md:text-4xl text-tb-navy font-semibold leading-tight"
          style={{ fontFamily: '"Playfair Display", serif' }}
          data-testid="bookmarks-title"
        >
          Bookmarks
        </h1>
      </div>
      <p className="text-tb-navy/60 font-hand text-sm mb-6">
        Every stay you&apos;ve saved lives here. Rates are live at the
        supplier — tap Book Now to pick up where you left off.
      </p>

      {loading && (
        <div
          className="flex items-center gap-2 text-tb-navy/60 font-hand text-sm py-6"
          data-testid="bookmarks-loading"
        >
          <Loader2 className="w-4 h-4 animate-spin" /> Loading your bookmarks…
        </div>
      )}

      {!loading && error && (
        <div
          className="rounded-2xl border-2 border-red-200 bg-red-50 text-red-700 font-hand text-sm p-4"
          data-testid="bookmarks-error"
        >
          {error}
        </div>
      )}

      {!loading && !error && rows.length === 0 && (
        <div
          className="rounded-2xl bg-white border-2 border-tb-navy/15 p-8 text-center"
          data-testid="bookmarks-empty"
        >
          <Bookmark className="w-8 h-8 text-tb-navy/40 mx-auto mb-3" />
          <p
            className="text-tb-navy text-lg"
            style={{ fontFamily: '"Playfair Display", serif' }}
          >
            No saved stays yet.
          </p>
          <p className="mt-1 text-tb-navy/60 font-hand text-sm">
            Tap the <Heart className="inline w-3.5 h-3.5 text-tb-coral" /> on
            any stay in Explore to add it here.
          </p>
          <button
            type="button"
            onClick={() => nav("/explore")}
            data-testid="bookmarks-empty-cta"
            className="mt-5 h-10 px-5 rounded-full bg-tb-gold-bright text-tb-navy font-hand text-sm font-semibold inline-flex items-center gap-2 hover:brightness-110 tb-tap"
          >
            Browse stays
          </button>
        </div>
      )}

      {!loading && !error && rows.length > 0 && (
        <div className="space-y-3 md:space-y-4" data-testid="bookmarks-list">
          {rows.map((bm) => (
            <BookmarkRow
              key={bm.id}
              bm={bm}
              onRemove={remove}
              onDream={dreamIt}
              dreaming={dreamingId === bm.id}
            />
          ))}
        </div>
      )}
    </div>
  );
}
